import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { TeamMemberWithUser } from '@/types/chat.types'

interface AgentResponse {
	id: string
	name: string | null
	email: string | null
	avatar: string | null
	jobTitle: string | null
	status: 'online' | 'offline' | 'away'
	statusMessage: string | null
	lastActiveAt: string | null
	isActive: boolean
}

/**
 * Fetches available agents for a specific program
 * This hook calls the /api/chat/programs/:programId/agents endpoint
 * and transforms the response to match the TeamMemberWithUser format
 * expected by the AssignmentDropdown component
 */
export function useProgramAgents(programId: string | undefined) {
	return useQuery<TeamMemberWithUser[], Error>({
		queryKey: ['program-agents', programId],
		queryFn: async () => {
			if (!programId) throw new Error('Program ID is required')

			const response = await apiClient<{ agents: AgentResponse[] }>(
				`/api/chat/programs/${programId}/agents`
			)

			// Transform the agent response to TeamMemberWithUser format
			// Note: We're using a simplified structure since the backend returns agent profiles
			// not full team member records. The AssignmentDropdown only needs user_id and user info.
			return response.agents.map((agent): TeamMemberWithUser => ({
				// Using agent.id as both id and user_id since backend returns user_id directly
				id: agent.id,
				user_id: agent.id,
				team_id: '', // Not needed for assignment dropdown
				role: 'member', // Default role, not used by assignment dropdown
				joined_at: '', // Not needed for assignment dropdown
				user: {
					id: agent.id,
					name: agent.name,
					email: agent.email || '',
					avatar_url: agent.avatar,
				},
			}))
		},
		enabled: !!programId,
	})
}
